import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { logAction, getCurrentAdmin } from '../services/logger';
import {
  dispatchWorkflow,
  listWorkflowRuns,
  getLatestRun,
  durationSeconds,
  hasGitHubToken,
  type WorkflowRun,
} from '../services/github';
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  ExternalLink,
  Trophy,
  Calendar,
  Globe,
  Activity,
  ShieldAlert,
  Eye,
  Users as UsersIcon,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast from 'react-hot-toast';

const WORKFLOW_FILE = 'sync-matches.yml';

interface DbStats {
  totalMatches: number;
  cdm2026Matches: number;
  weekMatches: number;
  lastSyncAt: string | null;
}

interface CompetitionConfig {
  id: number;
  name: string;
  sport: string;
  source: string;
  is_active: boolean;
  updated_at: string;
}

interface CompetitionStats {
  totalMatches: number;
  nextMatchDate: string | null;
  lastMatchDate: string | null;
}

type CompetitionWithStats = CompetitionConfig & CompetitionStats;

function statusIcon(run: WorkflowRun) {
  if (run.status !== 'completed') {
    return <Clock size={14} style={{ color: 'var(--orange)' }} />;
  }
  if (run.conclusion === 'success') {
    return <CheckCircle2 size={14} style={{ color: 'var(--green)' }} />;
  }
  return <XCircle size={14} style={{ color: 'var(--red)' }} />;
}

function formatDuration(sec: number | null): string {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

export function SyncPage() {
  const [stats, setStats] = useState<DbStats>({
    totalMatches: 0,
    cdm2026Matches: 0,
    weekMatches: 0,
    lastSyncAt: null,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [runs, setRuns] = useState<WorkflowRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [dispatching, setDispatching] = useState(false);
  const [polling, setPolling] = useState(false);
  const [competitions, setCompetitions] = useState<CompetitionWithStats[]>([]);
  const [competitionsLoading, setCompetitionsLoading] = useState(true);
  const [competitionsError, setCompetitionsError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const today = new Date();
      const todayIso = today.toISOString().split('T')[0];
      const weekEnd = new Date(today.getTime() + 7 * 86400000).toISOString().split('T')[0];

      const [total, cdm, week, lastSync] = await Promise.all([
        supabase
          .from('matches')
          .select('id', { count: 'exact', head: true }),
        supabase
          .from('matches')
          .select('id', { count: 'exact', head: true })
          .or(
            'competition.ilike.%coupe du monde%,competition.ilike.%world cup%,competition.ilike.%fifa%',
          ),
        supabase
          .from('matches')
          .select('id', { count: 'exact', head: true })
          .gte('match_date', todayIso)
          .lte('match_date', weekEnd),
        getLatestRunSafe(),
      ]);

      setStats({
        totalMatches: total.count || 0,
        cdm2026Matches: cdm.count || 0,
        weekMatches: week.count || 0,
        lastSyncAt: lastSync ? lastSync.updated_at : null,
      });
    } catch (err) {
      console.error('Load sync stats error:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadCompetitions = useCallback(async () => {
    setCompetitionsLoading(true);
    setCompetitionsError(null);
    try {
      // Fetch config + tous les matchs (id, competition, match_date) en parallèle
      // On group les stats côté client : plus simple que RPC avec GROUP BY pour ~200-300 matchs.
      const todayIso = new Date().toISOString().split('T')[0];
      const [configRes, matchesRes] = await Promise.all([
        supabase
          .from('competition_config')
          .select('*')
          .order('sport', { ascending: true })
          .order('name', { ascending: true }),
        supabase.from('matches').select('id, competition, match_date'),
      ]);

      if (configRes.error) throw configRes.error;
      if (matchesRes.error) throw matchesRes.error;

      // Index matches par nom de compétition (case-insensitive, trim)
      const statsByComp = new Map<string, { total: number; next: string | null; last: string | null }>();
      for (const m of (matchesRes.data || []) as Array<{ competition: string | null; match_date: string | null }>) {
        const key = (m.competition || '').trim().toLowerCase();
        if (!key) continue;
        const entry = statsByComp.get(key) || { total: 0, next: null, last: null };
        entry.total += 1;
        if (m.match_date) {
          if (!entry.last || m.match_date > entry.last) entry.last = m.match_date;
          if (m.match_date >= todayIso && (!entry.next || m.match_date < entry.next)) {
            entry.next = m.match_date;
          }
        }
        statsByComp.set(key, entry);
      }

      const withStats: CompetitionWithStats[] = ((configRes.data || []) as CompetitionConfig[]).map(c => {
        const key = c.name.trim().toLowerCase();
        const s = statsByComp.get(key);
        return {
          ...c,
          totalMatches: s?.total ?? 0,
          nextMatchDate: s?.next ?? null,
          lastMatchDate: s?.last ?? null,
        };
      });

      setCompetitions(withStats);
    } catch (err: any) {
      console.error('Load competitions error:', err);
      setCompetitionsError(err?.message || 'Erreur de chargement');
    } finally {
      setCompetitionsLoading(false);
    }
  }, []);

  const handleToggleCompetition = async (comp: CompetitionWithStats) => {
    const next = !comp.is_active;
    setTogglingId(comp.id);
    // Optimistic update
    setCompetitions(prev =>
      prev.map(c => (c.id === comp.id ? { ...c, is_active: next } : c)),
    );
    try {
      const { error } = await supabase
        .from('competition_config')
        .update({ is_active: next, updated_at: new Date().toISOString() })
        .eq('id', comp.id);
      if (error) throw error;

      const admin = getCurrentAdmin();
      await logAction(
        'toggle_competition',
        'competition_config',
        String(comp.id),
        { is_active: comp.is_active },
        { is_active: next },
        `${admin?.name || 'Admin'} a ${next ? 'activé' : 'désactivé'} la compétition ${comp.name}`,
      );
      toast.success(
        next
          ? `${comp.name} activée — sera syncée au prochain run`
          : `${comp.name} désactivée — plus syncée`,
      );
    } catch (err: any) {
      console.error('Toggle competition error:', err);
      // Rollback
      setCompetitions(prev =>
        prev.map(c => (c.id === comp.id ? { ...c, is_active: comp.is_active } : c)),
      );
      toast.error(err?.message || 'Erreur toggle compétition');
    } finally {
      setTogglingId(null);
    }
  };

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    setRunsError(null);
    try {
      if (!hasGitHubToken()) {
        setRunsError('REACT_APP_GITHUB_TOKEN manquant');
        setRuns([]);
        return;
      }
      const list = await listWorkflowRuns(WORKFLOW_FILE, 5);
      setRuns(list);
    } catch (err: any) {
      console.error('Load runs error:', err);
      setRunsError(err?.message || 'Erreur GitHub API');
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadRuns();
    loadCompetitions();
  }, [loadStats, loadRuns, loadCompetitions]);

  const handleDispatch = async () => {
    if (!hasGitHubToken()) {
      toast.error('Configure REACT_APP_GITHUB_TOKEN dans admin/.env');
      return;
    }
    if (!window.confirm('Forcer la synchronisation des matchs maintenant ?')) return;

    setDispatching(true);
    const triggeredAt = new Date().toISOString();
    try {
      await dispatchWorkflow(WORKFLOW_FILE);
      const admin = getCurrentAdmin();
      await logAction(
        'trigger_sync',
        'workflow',
        null,
        null,
        { workflow: WORKFLOW_FILE },
        `${admin?.name || 'Admin'} a déclenché la synchronisation manuelle des matchs`,
      );
      toast.success('Sync déclenchée — en attente du run…');

      // Poll for the new run
      setPolling(true);
      let attempts = 0;
      const pollInterval = setInterval(async () => {
        attempts += 1;
        try {
          const list = await listWorkflowRuns(WORKFLOW_FILE, 5);
          setRuns(list);
          const newRun = list.find(
            r => new Date(r.created_at).getTime() >= new Date(triggeredAt).getTime() - 2000,
          );
          if (newRun && newRun.status === 'completed') {
            clearInterval(pollInterval);
            setPolling(false);
            if (newRun.conclusion === 'success') {
              toast.success(`✅ Sync terminée en ${formatDuration(durationSeconds(newRun))}`);
              loadStats();
            } else {
              toast.error(`❌ Sync échouée (${newRun.conclusion || 'unknown'})`);
            }
          }
        } catch (e) {
          // Ignore transient errors
        }
        if (attempts > 60) {
          clearInterval(pollInterval);
          setPolling(false);
          toast('Polling arrêté après 5 min — vérifie GitHub Actions', { icon: '⌛' });
        }
      }, 5000);
    } catch (err: any) {
      console.error('Dispatch error:', err);
      toast.error(err?.message || 'Erreur dispatch');
    } finally {
      setDispatching(false);
    }
  };

  const lastSyncLabel = stats.lastSyncAt
    ? formatDistanceToNow(new Date(stats.lastSyncAt), { addSuffix: true, locale: fr })
    : '—';

  return (
    <div>
      <h2
        style={{
          fontSize: '20px',
          fontWeight: 700,
          margin: '0 0 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <RefreshCw size={20} style={{ color: 'var(--accent)' }} />
        Synchronisation matchs
      </h2>

      {!hasGitHubToken() && (
        <div
          style={{
            padding: '10px 12px',
            marginBottom: '14px',
            background: 'var(--orange-dim)',
            border: '1px solid rgba(198,122,0,0.25)',
            borderRadius: '8px',
            color: 'var(--orange)',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <AlertTriangle size={14} />
          Configure <code style={{ fontFamily: 'monospace' }}>REACT_APP_GITHUB_TOKEN</code> dans{' '}
          <code style={{ fontFamily: 'monospace' }}>admin/.env</code> puis rebuild pour activer le
          déclenchement manuel.
        </div>
      )}

      {/* Stats DB */}
      <div className="stats-grid">
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Matchs en base</div>
              <div className="stat-value">{statsLoading ? '…' : stats.totalMatches}</div>
              <div className="stat-sub">toutes compétitions</div>
            </div>
            <Trophy size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Coupe du Monde 2026</div>
              <div className="stat-value" style={{ color: 'var(--accent)' }}>
                {statsLoading ? '…' : stats.cdm2026Matches}
              </div>
              <div className="stat-sub">matchs FIFA</div>
            </div>
            <Globe size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Cette semaine</div>
              <div className="stat-value" style={{ color: 'var(--green)' }}>
                {statsLoading ? '…' : stats.weekMatches}
              </div>
              <div className="stat-sub">J+0 → J+7</div>
            </div>
            <Calendar size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Dernière sync</div>
              <div
                className="stat-value"
                style={{ fontSize: stats.lastSyncAt ? '18px' : '26px' }}
              >
                {statsLoading ? '…' : lastSyncLabel}
              </div>
              <div className="stat-sub">GitHub Actions</div>
            </div>
            <Clock size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div
        className="dashboard-2col"
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '16px',
          marginBottom: '16px',
        }}
      >
        <div className="card">
          <h3 className="card-title">Déclencher une sync manuelle</h3>
          <p className="muted" style={{ fontSize: '12px', marginBottom: '12px' }}>
            Le cron tourne chaque jour à 3h UTC. Utilise ce bouton pour forcer la sync
            maintenant (ex : après import de matchs manuel).
          </p>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              onClick={handleDispatch}
              disabled={dispatching || polling || !hasGitHubToken()}
            >
              <RefreshCw
                size={14}
                style={{
                  animation: polling ? 'spin 1.5s linear infinite' : undefined,
                }}
              />
              {polling
                ? 'Sync en cours…'
                : dispatching
                ? 'Déclenchement…'
                : 'Forcer la sync matchs'}
            </button>

            <button
              className="btn"
              title="Non implémenté — nécessite un script TheSportsDB league 4529 dédié"
              disabled
              style={{ opacity: 0.5, cursor: 'not-allowed' }}
            >
              <Trophy size={14} />
              Sync Coupe de France (à venir)
            </button>
          </div>

          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>

        <div className="card">
          <h3 className="card-title">Source des données</h3>
          <div style={{ fontSize: '12px', lineHeight: '1.7', color: 'var(--text-muted)' }}>
            <div>
              <strong>API :</strong> football-data.org v4
            </div>
            <div>
              <strong>Compétitions :</strong> L1, PL, CL, Bundes, Liga, Serie A
            </div>
            <div>
              <strong>Fenêtre :</strong> J+0 → J+30
            </div>
            <div>
              <strong>Diffusions :</strong> bars proba ≥ 50% · is_visible
            </div>
          </div>
        </div>
      </div>

      {/* Compétitions suivies */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '4px',
          }}
        >
          <h3 className="card-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity size={16} style={{ color: 'var(--accent)' }} />
            Compétitions suivies
          </h3>
          <button
            className="btn-icon"
            onClick={loadCompetitions}
            disabled={competitionsLoading}
            title="Rafraîchir"
          >
            <RefreshCw
              size={14}
              style={{ animation: competitionsLoading ? 'spin 1s linear infinite' : undefined }}
            />
          </button>
        </div>
        <p className="muted" style={{ fontSize: '12px', marginTop: '6px', marginBottom: '14px' }}>
          Whitelist lue par <code style={{ fontFamily: 'monospace' }}>sync-matches.js</code>. Désactive une
          compétition ici pour l'exclure des prochaines syncs sans supprimer les matchs en base.
        </p>

        {competitionsError ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--red)',
              fontSize: '13px',
            }}
          >
            <ShieldAlert size={14} />
            {competitionsError}
          </div>
        ) : competitionsLoading ? (
          <p className="muted">Chargement…</p>
        ) : competitions.length === 0 ? (
          <div
            style={{
              padding: '12px',
              background: 'var(--orange-dim)',
              borderRadius: '8px',
              color: 'var(--orange)',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <AlertTriangle size={14} />
            La table <code style={{ fontFamily: 'monospace' }}>competition_config</code> est vide —
            exécute la migration 006 dans Supabase.
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '10px',
            }}
          >
            {competitions.map(comp => {
              const isFootball = comp.sport.toLowerCase() === 'football';
              const sportColor = isFootball ? 'var(--green)' : '#3B82F6';
              const sportBg = isFootball ? 'rgba(48,209,88,0.12)' : 'rgba(59,130,246,0.12)';
              const sourceLabel =
                comp.source === 'football-data'
                  ? 'football-data.org'
                  : comp.source === 'thesportsdb'
                  ? 'TheSportsDB'
                  : comp.source;
              const nextDate = comp.nextMatchDate
                ? format(new Date(comp.nextMatchDate + 'T00:00:00'), 'dd MMM', { locale: fr })
                : '—';
              const lastDate = comp.lastMatchDate
                ? format(new Date(comp.lastMatchDate + 'T00:00:00'), 'dd MMM', { locale: fr })
                : '—';

              return (
                <div
                  key={comp.id}
                  style={{
                    padding: '12px 14px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '10px',
                    opacity: comp.is_active ? 1 : 0.55,
                    transition: 'opacity 150ms ease',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '8px',
                      marginBottom: '10px',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: 700,
                          color: 'var(--text)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {comp.name}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                        <span
                          style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '10px',
                            fontWeight: 700,
                            color: sportColor,
                            background: sportBg,
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                          }}
                        >
                          {comp.sport}
                        </span>
                        <span
                          style={{
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '10px',
                            color: 'var(--text-muted)',
                            background: 'var(--bg-hover)',
                          }}
                        >
                          {sourceLabel}
                        </span>
                      </div>
                    </div>

                    {/* Toggle */}
                    <button
                      onClick={() => handleToggleCompetition(comp)}
                      disabled={togglingId === comp.id}
                      title={comp.is_active ? 'Désactiver' : 'Activer'}
                      aria-label={comp.is_active ? 'Désactiver' : 'Activer'}
                      style={{
                        position: 'relative',
                        flexShrink: 0,
                        width: '40px',
                        height: '22px',
                        borderRadius: '11px',
                        border: 'none',
                        background: comp.is_active ? 'var(--green)' : 'var(--bg-hover)',
                        cursor: togglingId === comp.id ? 'wait' : 'pointer',
                        transition: 'background 150ms ease',
                        padding: 0,
                        opacity: togglingId === comp.id ? 0.6 : 1,
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '3px',
                          left: comp.is_active ? '21px' : '3px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: '#fff',
                          transition: 'left 150ms ease',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        }}
                      />
                    </button>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '6px',
                      fontSize: '11px',
                      marginBottom: '10px',
                    }}
                  >
                    <div>
                      <div style={{ color: 'var(--text-dim)', marginBottom: '2px' }}>Matchs</div>
                      <div style={{ fontWeight: 700, color: 'var(--text)' }}>{comp.totalMatches}</div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-dim)', marginBottom: '2px' }}>Prochain</div>
                      <div style={{ fontWeight: 600, color: 'var(--accent)' }}>{nextDate}</div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-dim)', marginBottom: '2px' }}>Dernier</div>
                      <div style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{lastDate}</div>
                    </div>
                  </div>

                  {/* Drill-down vers /visibility filtré sur cette compétition */}
                  <div style={{ display: 'flex', gap: '6px', borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
                    <Link
                      to={`/visibility?tab=matches&competition=${encodeURIComponent(comp.name)}`}
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                        padding: '6px 8px', fontSize: '11px', fontWeight: 600,
                        borderRadius: '6px', background: 'var(--bg-hover)', color: 'var(--text)',
                        textDecoration: 'none', transition: 'background 0.1s',
                      }}
                      title={`Voir les matchs de ${comp.name}`}
                    >
                      <Eye size={12} /> Matchs
                    </Link>
                    <Link
                      to={`/visibility?tab=teams&competition=${encodeURIComponent(comp.name)}&sport=${encodeURIComponent(comp.sport)}`}
                      style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                        padding: '6px 8px', fontSize: '11px', fontWeight: 600,
                        borderRadius: '6px', background: 'var(--bg-hover)', color: 'var(--text)',
                        textDecoration: 'none', transition: 'background 0.1s',
                      }}
                      title={`Voir les équipes ${comp.sport}`}
                    >
                      <UsersIcon size={12} /> Équipes
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Historique runs */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 className="card-title" style={{ margin: 0 }}>
            Historique — 5 dernières syncs
          </h3>
          <button
            className="btn-icon"
            onClick={loadRuns}
            disabled={runsLoading}
            title="Rafraîchir"
          >
            <RefreshCw size={14} style={{ animation: runsLoading ? 'spin 1s linear infinite' : undefined }} />
          </button>
        </div>

        {runsError ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--red)',
              fontSize: '13px',
              marginTop: '10px',
            }}
          >
            <AlertTriangle size={14} />
            {runsError}
          </div>
        ) : runsLoading ? (
          <p className="muted">Chargement…</p>
        ) : runs.length === 0 ? (
          <p className="muted">Aucun run trouvé</p>
        ) : (
          <table className="data-table" style={{ marginTop: '8px' }}>
            <thead>
              <tr>
                <th style={{ width: '24px' }}></th>
                <th>Date</th>
                <th>Durée</th>
                <th>Déclencheur</th>
                <th>#</th>
                <th style={{ textAlign: 'right' }}>Lien</th>
              </tr>
            </thead>
            <tbody>
              {runs.map(run => {
                const dur = durationSeconds(run);
                return (
                  <tr key={run.id}>
                    <td>{statusIcon(run)}</td>
                    <td style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {format(new Date(run.created_at), "dd/MM HH'h'mm", { locale: fr })}
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {formatDuration(dur)}
                    </td>
                    <td style={{ fontSize: '12px' }}>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: 700,
                          color: run.event === 'schedule' ? 'var(--text-dim)' : 'var(--accent)',
                          background:
                            run.event === 'schedule'
                              ? 'var(--bg-hover)'
                              : 'var(--accent-dim)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {run.event === 'schedule' ? 'Cron' : 'Manuel'}
                      </span>
                    </td>
                    <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      #{run.run_number}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <a
                        href={run.html_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-icon"
                        title="Ouvrir sur GitHub"
                      >
                        <ExternalLink size={13} />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Helper isolé pour attraper l'erreur token → pas de stack GitHub dans load stats
async function getLatestRunSafe(): Promise<WorkflowRun | null> {
  try {
    if (!hasGitHubToken()) return null;
    return await getLatestRun(WORKFLOW_FILE);
  } catch {
    return null;
  }
}

